import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  CommissionRuleAction,
  CommissionRuleDto,
  DisputeAction,
  DisputeDto,
  ExceptionAction,
  ExceptionDto,
  ListParams,
  PaginationMeta,
  PayoutAction,
  PayoutDto,
  PayoutLineItemDto,
  PayoutLinePreview,
  RefundAction,
  RefundDto,
  RefundCustomerStatusHistory,
  RequestMeta,
  SettlementAction,
  SettlementDto,
  TransactionAction,
  TransactionDto,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/finance`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createFinanceApi(client: ApiClient) {
  return {
    listTransactions(params: ListParams = {}) {
      return client.get<{
        transactions: TransactionDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/transactions${toQuery(params)}`);
    },
    getTransaction(id: string) {
      return client.get<{ transaction: TransactionDto; meta: RequestMeta }>(
        `${BASE}/transactions/${id}`,
      );
    },
    createTransaction(body: Record<string, unknown>) {
      return client.post<{ transaction: TransactionDto; meta: RequestMeta }>(
        `${BASE}/transactions`,
        body,
        newIdempotencyKey(),
      );
    },
    createAdjustment(body: Record<string, unknown>) {
      return client.post<{ transaction: TransactionDto; meta: RequestMeta }>(
        `${BASE}/transactions/adjustments`,
        body,
        newIdempotencyKey(),
      );
    },
    applyTransactionAction(id: string, action: TransactionAction, body: { reason?: string }) {
      return client.post(
        `${BASE}/transactions/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listRefunds(params: ListParams = {}) {
      return client.get<{
        refunds: RefundDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/refunds${toQuery(params)}`);
    },
    getRefund(id: string) {
      return client.get<{ refund: RefundDto; meta: RequestMeta }>(
        `${BASE}/refunds/${id}`,
      );
    },
    requestRefund(body: Record<string, unknown>) {
      return client.post<{ refund: RefundDto; meta: RequestMeta }>(
        `${BASE}/refunds`,
        body,
        newIdempotencyKey(),
      );
    },
    /** i37 — the customer-facing status and its history. */
    getRefundCustomerStatus(id: string) {
      return client.get<RefundCustomerStatusHistory & { meta: RequestMeta }>(
        `${BASE}/refunds/${id}/customer-status`,
      );
    },
    setRefundCustomerStatus(id: string, body: { customer_status: string; internal_note?: string }) {
      return client.post<{ refund: RefundDto; meta: RequestMeta }>(
        `${BASE}/refunds/${id}/customer-status`,
        body,
        newIdempotencyKey(),
      );
    },
    /** i36 — resend a FAILED provider refund (new attempt key). */
    retryProviderRefund(id: string, body: { reason?: string }) {
      return client.post<{ refund: RefundDto; meta: RequestMeta }>(
        `${BASE}/refunds/${id}/provider-retry`,
        body,
        newIdempotencyKey(),
      );
    },
    applyRefundAction(
      id: string,
      action: RefundAction,
      body: { reason?: string; approval_id?: string },
    ) {
      return client.post(
        `${BASE}/refunds/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listPayouts(params: ListParams = {}) {
      return client.get<{
        payouts: PayoutDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/payouts${toQuery(params)}`);
    },
    getPayout(id: string) {
      return client.get<{ payout: PayoutDto; meta: RequestMeta }>(
        `${BASE}/payouts/${id}`,
      );
    },
    listPayoutLineItems(id: string) {
      return client.get<{
        payout: PayoutDto;
        line_items: PayoutLineItemDto[];
        meta: RequestMeta;
      }>(`${BASE}/payouts/${id}/line-items`);
    },
    /** What adding this transaction would record, with a venue split in words. */
    previewPayoutLineItem(payoutId: string, transactionId: string) {
      return client.get<{ preview: PayoutLinePreview; meta: RequestMeta }>(
        `${BASE}/payouts/${payoutId}/line-items/preview?transaction_id=${encodeURIComponent(transactionId)}`,
      );
    },
    createPayout(body: Record<string, unknown>) {
      return client.post<{ payout: PayoutDto; meta: RequestMeta }>(
        `${BASE}/payouts`,
        body,
        newIdempotencyKey(),
      );
    },
    addPayoutLineItem(payoutId: string, body: Record<string, unknown>) {
      return client.post<{ line_item: PayoutLineItemDto; meta: RequestMeta }>(
        `${BASE}/payouts/${payoutId}/line-items`,
        body,
        newIdempotencyKey(),
      );
    },
    applyPayoutAction(
      id: string,
      action: PayoutAction,
      body: { reason?: string; approval_id?: string; hold_reason?: string },
    ) {
      return client.post(
        `${BASE}/payouts/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listSettlements(params: ListParams = {}) {
      return client.get<{
        settlements: SettlementDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/settlements${toQuery(params)}`);
    },
    getSettlement(id: string) {
      return client.get<{ settlement: SettlementDto; meta: RequestMeta }>(
        `${BASE}/settlements/${id}`,
      );
    },
    createSettlement(body: Record<string, unknown>) {
      return client.post<{ settlement: SettlementDto; meta: RequestMeta }>(
        `${BASE}/settlements`,
        body,
        newIdempotencyKey(),
      );
    },
    applySettlementAction(
      id: string,
      action: SettlementAction,
      body: {
        reason?: string;
        settled_amount?: number;
        settlement_reference?: string;
      },
    ) {
      return client.post(
        `${BASE}/settlements/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listCommissionRules(params: ListParams = {}) {
      return client.get<{
        commission_rules: CommissionRuleDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/commission-rules${toQuery(params)}`);
    },
    getCommissionRule(id: string) {
      return client.get<{ commission_rule: CommissionRuleDto; meta: RequestMeta }>(
        `${BASE}/commission-rules/${id}`,
      );
    },
    createCommissionRule(body: Record<string, unknown>) {
      return client.post<{ commission_rule: CommissionRuleDto; meta: RequestMeta }>(
        `${BASE}/commission-rules`,
        body,
        newIdempotencyKey(),
      );
    },
    applyCommissionAction(
      id: string,
      action: CommissionRuleAction,
      body: { reason?: string; approval_id?: string },
    ) {
      return client.post(
        `${BASE}/commission-rules/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listExceptions(params: ListParams = {}) {
      return client.get<{
        exceptions: ExceptionDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/exceptions${toQuery(params)}`);
    },
    getException(id: string) {
      return client.get<{ exception: ExceptionDto; meta: RequestMeta }>(
        `${BASE}/exceptions/${id}`,
      );
    },
    createException(body: Record<string, unknown>) {
      return client.post<{ exception: ExceptionDto; meta: RequestMeta }>(
        `${BASE}/exceptions`,
        body,
        newIdempotencyKey(),
      );
    },
    applyExceptionAction(
      id: string,
      action: ExceptionAction,
      body: { reason?: string; resolution_reason?: string; owner_user_id?: string },
    ) {
      return client.post(
        `${BASE}/exceptions/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listDisputes(params: ListParams = {}) {
      return client.get<{
        disputes: DisputeDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/disputes${toQuery(params)}`);
    },
    getDispute(id: string) {
      return client.get<{ dispute: DisputeDto; meta: RequestMeta }>(
        `${BASE}/disputes/${id}`,
      );
    },
    openDispute(body: Record<string, unknown>) {
      return client.post<{ dispute: DisputeDto; meta: RequestMeta }>(
        `${BASE}/disputes`,
        body,
        newIdempotencyKey(),
      );
    },
    applyDisputeAction(
      id: string,
      action: DisputeAction,
      body: { reason?: string; owner_user_id?: string },
    ) {
      return client.post(
        `${BASE}/disputes/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type FinanceApi = ReturnType<typeof createFinanceApi>;
